// src/components/FeedbackPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import './FeedbackPage.css';
import { ToastContainer, toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../config';
import { FaStar } from 'react-icons/fa';
import Header from './Header';

export default function FeedbackPage() {
  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [modelsUsed, setModelsUsed] = useState([]);
  const [fileFormats, setFileFormats] = useState([]);
  const [mostAccurate, setMostAccurate] = useState('');
  const [usefulFeatures, setUsefulFeatures] = useState('');
  const [suggestions, setSuggestions] = useState('');
  const [bonusFeature, setBonusFeature] = useState('');
  const [rating, setRating] = useState(0);
  const [userRole, setUserRole] = useState('');
  const [firstTimeUser, setFirstTimeUser] = useState('');
  const [mostTrustedCategory, setMostTrustedCategory] = useState('');
  const [activeStep, setActiveStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);

  const modelOptions = [
    'Xception', 'EfficientB4', 'ViT-B/16',
    'F3Net', 'SPSL', 'SRM',
    'UCF', 'UnivFD', 'CORE',
    'DAW-FDD', 'DAG-FDD', 'PG-FDD'
  ];

  const formatOptions = ['Image', 'Audio', 'Video'];
  const categoryOptions = ['Naive', 'Frequency', 'Spatial', 'Fairness-enhanced'];
  const userRoles = ['Researcher', 'Developer', 'Student', 'General User'];

  // Refs to avoid duplicated side effects/toasts in React 18 StrictMode (dev)
  const welcomedRef = useRef(false);
  const navigatedRef = useRef(false);

  // 🔐 Check session
  useEffect(() => {
    let cancelled = false;

    fetch(`${BASE_URL}/verify-session`, {
      method: 'GET',
      credentials: 'include'
    })
      .then(res => {
        if (!res.ok) throw new Error('Unauthorized');
        return res.json();
      })
      .then(() => {
        if (cancelled) return;
        setIsAuthenticated(true);
        if (!welcomedRef.current) {
          welcomedRef.current = true;
          toast.info('Welcome! Kindly answer each section to submit your feedback.', {
            position: 'top-center',
            autoClose: 6000,
            transition: Slide,
            toastId: 'welcome-info', // ✅ prevents duplicate toast
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (!navigatedRef.current) {
          navigatedRef.current = true;
          toast.error('Please login to submit feedback.', { toastId: 'auth-error' });
          navigate('/login');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (submitted) {
      const t = setTimeout(() => {
        navigate('/');
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [submitted, navigate]);

  const advanceStep = (current) => {
    if (activeStep === current) {
      setActiveStep(current + 1);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.warning('Please rate your experience!', { toastId: 'rate-warning' });
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          modelsUsed,
          fileFormats,
          mostAccurateModel: mostAccurate,
          usefulFeatures,
          suggestions,
          bonusFeature,
          rating,
          userRole,
          firstTimeUser,
          mostTrustedCategory
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'Thanks for your feedback!', { toastId: 'fb-success' });
        setSubmitted(true);
      } else {
        toast.error(data.detail || 'Submission failed', { toastId: 'fb-fail' });
      }
    } catch (error) {
      toast.error('Error connecting to server', { toastId: 'fb-network' });
    }
  };

  return (
    <div className="feedback-container">
      {/* ❌ Removed the "For more inquiries" top bar as requested */}

      {/* Keep a single ToastContainer; limit guards burst toasts from other places */}
      <ToastContainer position="top-center" newestOnTop limit={3} />

      <div className="feedback-disclaimer">
        If you have additional questions or wish to report an issue with our services, please contact:
        <a href="mailto:m2lab.purdue@gmail.com"> m2lab.purdue@gmail.com</a>
      </div>

      <Header sticky={true} />

      {isAuthenticated && !submitted && (
        <div className="feedback-box">
          <h2>Share Your Feedback</h2>

          {/* Step 1 */}
          <label className={activeStep === 1 ? 'active-label' : ''}>Which model(s) did you find interesting?</label>
          <div className="multi-checkbox">
            {modelOptions.map((model) => (
              <label key={model}>
                <input
                  type="checkbox"
                  value={model}
                  checked={modelsUsed.includes(model)}
                  onChange={(e) => {
                    const value = e.target.value;
                    const updated = modelsUsed.includes(value)
                      ? modelsUsed.filter((m) => m !== value)
                      : [...modelsUsed, value];
                    setModelsUsed(updated);
                    if (updated.length > 0) advanceStep(1);
                  }}
                />
                <span>{model}</span>
              </label>
            ))}
          </div>

          {/* Step 2 */}
          <label className={activeStep === 2 ? 'active-label' : ''}>Which file formats did you use?</label>
          <div className="multi-checkbox">
            {formatOptions.map((format) => (
              <label key={format}>
                <input
                  type="checkbox"
                  value={format}
                  checked={fileFormats.includes(format)}
                  onChange={(e) => {
                    const value = e.target.value;
                    const updated = fileFormats.includes(value)
                      ? fileFormats.filter((f) => f !== value)
                      : [...fileFormats, value];
                    setFileFormats(updated);
                    if (updated.length > 0) advanceStep(2);
                  }}
                />
                <span>{format}</span>
              </label>
            ))}
          </div>

          {/* Step 3 */}
          <label className={activeStep === 3 ? 'active-label' : ''}>Which model was most accurate?</label>
          <select
            value={mostAccurate}
            onChange={(e) => {
              setMostAccurate(e.target.value);
              advanceStep(3);
            }}
          >
            <option value="">Select a model</option>
            {modelOptions.map((model) => (
              <option key={model} value={model}>{model}</option>
            ))}
          </select>

          {/* Step 4 */}
          <label className={activeStep === 4 ? 'active-label' : ''}>Which features of this platform did you find useful?</label>
          <input
            type="text"
            value={usefulFeatures}
            onChange={(e) => {
              setUsefulFeatures(e.target.value);
              if (e.target.value.length > 3) advanceStep(4);
            }}
            placeholder="Example: Face-level analysis, Model comparison..."
          />

          {/* Step 5 */}
          <label className={activeStep === 5 ? 'active-label' : ''}>What improvements would you like to see?</label>
          <textarea
            maxLength={500}
            value={suggestions}
            onChange={(e) => {
              setSuggestions(e.target.value);
              if (e.target.value.length > 3) advanceStep(5);
            }}
            placeholder="Tell us what you'd like to see in future updates..."
          />

          {/* Step 6 */}
          <label className={activeStep === 6 ? 'active-label' : ''}>Rate your overall experience:</label>
          <div className="star-rating">
            {[1, 2, 3, 4, 5].map((num) => (
              <FaStar
                key={num}
                size={24}
                color={num <= rating ? '#00f2fe' : '#ccc'}
                onClick={() => {
                  setRating(num);
                  advanceStep(6);
                }}
                className="star"
              />
            ))}
          </div>

          {/* Step 7 */}
          <label className={activeStep === 7 ? 'active-label' : ''}>Any bonus ideas or features you’d love to see?</label>
          <input
            type="text"
            value={bonusFeature}
            onChange={(e) => {
              setBonusFeature(e.target.value);
              if (e.target.value.length > 3) advanceStep(7);
            }}
            placeholder="Ex: Video-level forgery detection, Mobile App, etc."
          />

          {/* Step 8 */}
          <label className={activeStep === 8 ? 'active-label' : ''}>What is your role?</label>
          <select
            value={userRole}
            onChange={(e) => {
              setUserRole(e.target.value);
              advanceStep(8);
            }}
          >
            <option value="">Select role</option>
            {userRoles.map((role) => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>

          {/* Step 9 */}
          <label className={activeStep === 9 ? 'active-label' : ''}>Is this your first time using AI DeepFake tools?</label>
          <select
            value={firstTimeUser}
            onChange={(e) => {
              setFirstTimeUser(e.target.value);
              advanceStep(9);
            }}
          >
            <option value="">Select one</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>

          {/* Step 10 */}
          <label className={activeStep === 10 ? 'active-label' : ''}>Which model category do you trust the most?</label>
          <select
            value={mostTrustedCategory}
            onChange={(e) => {
              setMostTrustedCategory(e.target.value);
              advanceStep(10);
            }}
          >
            <option value="">Choose a category</option>
            {categoryOptions.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <button className="submit-feedback-btn" onClick={handleSubmit}>
            Submit Feedback
          </button>
        </div>
      )}

      {submitted && (
        <div className="submission-overlay">
          <button className="center-thankyou-btn">Thank you for your feedback!</button>
        </div>
      )}

      <footer className="footer-section">
        <div className="footer-left">© 2025 Purdue University</div>
        <div className="footer-center">
          Developed by Machine Learning and Media Forensics (M2) Lab from Purdue University
        </div>
        <div className="footer-right">
          <a href="#">Privacy Policy</a> | <a href="#">Terms of Use</a> | <a href="#">Contact</a>
        </div>
      </footer>

      <div className="floating-contact">
        <a href="mailto:m2lab.purdue@gmail.com">📩 Contact M2 Lab</a>
        <span className="contact-email-tooltip">m2lab.purdue@gmail.com</span>
      </div>
    </div>
  );
}
